const STORAGE_KEY = 'store-wallet-funding-attempt-links'

export type WalletFundingAttemptLink = {
  fundingAttemptId: string
  amount: number
  currency: string
  createdAt: string
  transactionId?: string
}

type WalletFundingAttemptLinksStore = {
  links: WalletFundingAttemptLink[]
}

const readStore = (): WalletFundingAttemptLinksStore => {
  if (typeof window === 'undefined') {
    return { links: [] }
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { links: [] }
    const parsed = JSON.parse(raw) as WalletFundingAttemptLinksStore
    return Array.isArray(parsed?.links) ? parsed : { links: [] }
  } catch {
    return { links: [] }
  }
}

const writeStore = (store: WalletFundingAttemptLinksStore) => {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch (error) {
    console.warn('Failed to write to sessionStorage:', error)
  }
}

export const rememberWalletFundingAttemptLink = (link: {
  fundingAttemptId: string
  amount: number
  currency: string
}) => {
  const store = readStore()
  const fundingAttemptId = String(link.fundingAttemptId || '').trim()
  if (!fundingAttemptId) return

  const nextLink: WalletFundingAttemptLink = {
    fundingAttemptId,
    amount: Math.abs(link.amount),
    currency: link.currency,
    createdAt: new Date().toISOString()
  }

  const withoutDuplicate = store.links.filter(
    item => item.fundingAttemptId !== fundingAttemptId
  )

  writeStore({
    links: [nextLink, ...withoutDuplicate].slice(0, 20)
  })
}

export const linkWalletFundingAttemptToTransaction = (
  fundingAttemptId: string,
  transactionId: string
) => {
  const normalizedFundingAttemptId = String(fundingAttemptId || '').trim()
  const normalizedTransactionId = String(transactionId || '').trim()
  if (!normalizedFundingAttemptId || !normalizedTransactionId) return

  const store = readStore()
  let changed = false
  const nextLinks = store.links.map(link => {
    if (link.fundingAttemptId === normalizedFundingAttemptId) {
      if (link.transactionId !== normalizedTransactionId) {
        changed = true
        return { ...link, transactionId: normalizedTransactionId }
      }
    }
    return link
  })

  if (changed) {
    writeStore({ links: nextLinks })
  }
}

export const resolveFundingAttemptIdForWalletTransaction = (input: {
  transactionId: string
  type: string
  status: string
  amount: number
  fundingAttemptId?: string
}): string | undefined => {
  const explicitId = String(input.fundingAttemptId || '').trim()
  if (explicitId) return explicitId

  const normalizedType = String(input.type || '').toUpperCase()
  const normalizedStatus = String(input.status || '').toLowerCase()
  if (!normalizedType.includes('CASH') || normalizedStatus !== 'pending') {
    return undefined
  }

  const transactionId = String(input.transactionId || '').trim()
  const absoluteAmount = Math.abs(input.amount)
  const store = readStore()

  const linked = store.links.find(link => link.transactionId === transactionId)
  if (linked?.fundingAttemptId) {
    return linked.fundingAttemptId
  }

  const amountMatches = store.links.filter(
    link =>
      !link.transactionId &&
      Math.abs(link.amount - absoluteAmount) < 0.001
  )

  if (amountMatches.length === 1) {
    return amountMatches[0]?.fundingAttemptId
  }

  return undefined
}
